import useDebounce from '@/platform/hooks/useDebounce';
import { Container, Header, Segment, SegmentHeader } from '@/platform/ui';
import {
    Combobox,
    ComboboxInput,
    ComboboxOption,
    ComboboxOptions,
    Dialog,
    DialogBackdrop,
    DialogPanel,
} from '@headlessui/react';
import {
    ArrowPathIcon,
    ArrowTopRightOnSquareIcon,
    MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { useForm, usePage } from '@inertiajs/react';
import { useEffect, useRef } from 'react';
import { CATEGORY_LABELS, SEARCH_HINTS } from './constants';

const Quickjump = () => {
    const { results, search } = usePage().props;
    const { data, setData, get, processing } = useForm({
        search: search,
    });

    const isInitial = useRef(true);

    const debouncedSearch = useDebounce(data?.search);

    useEffect(() => {
        if (isInitial.current) {
            isInitial.current = false;
            return;
        }

        if (debouncedSearch?.length >= 3) {
            get('/quickjump', {
                preserveScroll: true,
                preserveState: true,
                replace: true,
            });
        }
    }, [debouncedSearch]);

    return (
        <Container fluid>
            <Segment>
                <SegmentHeader />
            </Segment>

            <Dialog
                className='relative z-50'
                open={true}
                onClose={() => {
                    window.navigation.back();
                }}
            >
                <DialogBackdrop
                    transition
                    className='fixed inset-0 bg-black/85 transition-opacity data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in'
                />

                <div className='fixed inset-0 z-10 w-screen overflow-y-auto p-4 sm:p-6 md:p-20'>
                    <DialogPanel
                        transition
                        className='mx-auto max-w-xl transform divide-y divide-gray-100 overflow-hidden rounded-xl bg-white shadow-2xl outline-1 outline-black/5 transition-all data-closed:scale-95 data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in'
                    >
                        <Combobox>
                            <div className='grid grid-cols-1'>
                                <ComboboxInput
                                    autoFocus
                                    className='col-start-1 row-start-1 h-12 w-full border-0 pr-4 pl-11 text-base text-black outline-hidden placeholder:text-gray-400 focus:ring-0 sm:text-sm'
                                    placeholder='Zoeken...'
                                    value={data?.search || ''}
                                    onChange={event =>
                                        setData('search', event.target.value)
                                    }
                                    onBlur={() => {}}
                                />
                                {processing ? (
                                    <ArrowPathIcon
                                        className='pointer-events-none col-start-1 row-start-1 ml-4 size-5 animate-spin self-center text-gray-400'
                                        aria-hidden='true'
                                    />
                                ) : (
                                    <MagnifyingGlassIcon
                                        className='pointer-events-none col-start-1 row-start-1 ml-4 size-5 self-center text-gray-400'
                                        aria-hidden='true'
                                    />
                                )}

                                <kbd className='pointer-events-none col-start-1 row-start-1 mr-4 self-center justify-self-end rounded-sm border border-gray-300 px-1.5 py-0.5 font-sans text-xs text-gray-500'>
                                    Esc
                                </kbd>
                            </div>

                            {data?.search?.length >= 3 &&
                                Object.keys(results || {})?.length > 0 && (
                                    <ComboboxOptions
                                        static
                                        as='ul'
                                        className='max-h-80 scroll-pt-11 scroll-pb-2 space-y-2 divide-y-1 overflow-y-auto px-4 py-2'
                                    >
                                        {Object?.entries(results || {})?.map(
                                            ([category, items]) => (
                                                <li key={category}>
                                                    <h2 className='px-4 py-2.5 font-bold text-black'>
                                                        {
                                                            CATEGORY_LABELS[
                                                                category
                                                            ]
                                                        }
                                                    </h2>
                                                    <ul className='mb-4 text-sm text-black'>
                                                        {items?.map(item => (
                                                            <ComboboxOption
                                                                key={item.id}
                                                                value={item}
                                                                className='group mb-2 flex cursor-pointer items-center justify-between rounded-xl bg-gray-100 p-3 select-none data-focus:bg-gray-200 data-focus:outline-hidden'
                                                                as='li'
                                                            >
                                                                <a
                                                                    className='flex-auto'
                                                                    href={
                                                                        item.url
                                                                    }
                                                                >
                                                                    <p className='text-sm text-black group-data-focus:text-gray-900'>
                                                                        {
                                                                            item.label
                                                                        }
                                                                    </p>
                                                                    <p className='text-sm text-gray-500 group-data-focus:text-gray-700'>
                                                                        {
                                                                            item.sublabel
                                                                        }
                                                                    </p>
                                                                </a>

                                                                <ArrowTopRightOnSquareIcon className='text-primary-600 size-4' />
                                                            </ComboboxOption>
                                                        ))}
                                                    </ul>
                                                </li>
                                            )
                                        )}
                                    </ComboboxOptions>
                                )}

                            {data?.search?.length >= 3 &&
                                Object?.keys(results || {}).length === 0 && (
                                    <div className='flex flex-col items-center gap-2 py-10 text-gray-500'>
                                        <MagnifyingGlassIcon className='size-10 text-gray-700' />
                                        <Header
                                            as='h3'
                                            className='text-gray-700'
                                        >
                                            Geen resultaten gevonden
                                        </Header>
                                        <p className='text-sm'>
                                            Probeer je filters of zoekcriteria
                                            aan te passen.
                                        </p>
                                    </div>
                                )}
                            {data?.search?.length < 3 && (
                                <div className='max-h-80 overflow-y-auto px-6 py-5'>
                                    <h2 className='mb-3 px-2 text-xs font-semibold tracking-wider text-gray-400 uppercase'>
                                        Zoeken op
                                    </h2>
                                    <dl className='divide-y divide-gray-100'>
                                        {SEARCH_HINTS.map(hint => (
                                            <div
                                                key={hint.label}
                                                className='grid grid-cols-[8rem_1fr] items-center gap-x-4 rounded-lg px-2 py-2'
                                            >
                                                <dt className='text-sm font-medium text-gray-900'>
                                                    {hint.label}
                                                </dt>
                                                <dd className='flex items-center gap-x-3'>
                                                    <code className='rounded-md bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-600'>
                                                        {hint.example}
                                                    </code>
                                                    <span className='truncate text-xs text-gray-400'>
                                                        {hint.description}
                                                    </span>
                                                </dd>
                                            </div>
                                        ))}
                                    </dl>
                                </div>
                            )}
                        </Combobox>
                    </DialogPanel>
                </div>
            </Dialog>
        </Container>
    );
};

export default Quickjump;
